import React, { useState, useEffect } from 'react';
import { AlertCircle } from 'lucide-react';
import { useSites } from '../hooks/useSites';
import { useConfig } from '../hooks/useConfig';
import { useStats } from '../hooks/useStats';
import { updatePlacements, createSite as createSiteApi, updateFloors, updateConfig as updateConfigApi } from '../utils/api';
import apiClient from '../utils/api';
import { AVAILABLE_BIDDERS } from '../config/prebidOptions';
import { normalizePlacementData, createPlacementTemplate } from '../utils/dataNormalizers';
import PlacementEditModal from './PlacementEditModal';
import FloorEditModal from './FloorEditModal';
import SiteSelector from './SiteSelector';
import TabNavigation from './TabNavigation';
import ActionButtons from './ActionButtons';
import PlacementTable from './PlacementTable';
import SiteSettings from './SiteSettings';
import PrebidConfig from './PrebidConfig';
import PrivacyStorageTab from './PrivacyStorageTab';
import SiteManagementTab from './SiteManagementTab';
import AnalyticsTab from './AnalyticsTab';
import BidderManager from './BidderManager';
import ValidationErrorDisplay from './ValidationErrorDisplay';

const AdConfigDashboard = () => {
  console.log('🚀 [AdConfigDashboard] Component initializing - v2.0.1...');
  
  const [currentSite, setCurrentSite] = useState('lovedeco');
  const [activeView, setActiveView] = useState('placements');
  const [deploying, setDeploying] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedPlacements, setSelectedPlacements] = useState([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingPlacement, setEditingPlacement] = useState(null);
  const [editingPlacementName, setEditingPlacementName] = useState(null);
  const [floorModalOpen, setFloorModalOpen] = useState(false);
  const [floors, setFloors] = useState(null);
  const [floorsLoading, setFloorsLoading] = useState(false);
  const [bidderManagerOpen, setBidderManagerOpen] = useState(false);

  const { sites, loading: sitesLoading, error: sitesError, createSite, refetch: refetchSites } = useSites();
  console.log('🔍 [AdConfigDashboard] Sites data:', sites, 'Loading:', sitesLoading, 'Error:', sitesError);
  const { config, loading: configLoading, error: configError, updateConfig, deployConfig, updatePlacement, deletePlacement, addPlacement, refetch } = useConfig(currentSite);
  console.log('🔍 [AdConfigDashboard] Config data:', config, 'Loading:', configLoading, 'Error:', configError);
  
  const stats = useStats(config);
  console.log('🔍 [AdConfigDashboard] Stats data:', stats);


  // Auto-select site when sites load successfully
  useEffect(() => {
    if (!sitesLoading && !sitesError && Array.isArray(sites) && sites.length > 0) {
      // Get stored site preference
      const storedSite = localStorage.getItem('dashboard_selected_site');
      
      // Check if stored site exists in loaded sites
      const storedSiteExists = storedSite && sites.find(site => site.id === storedSite);
      
      // Check if current hardcoded site exists in loaded sites  
      const currentSiteExists = sites.find(site => site.id === currentSite);
      
      if (storedSiteExists) {
        console.log('🔍 [AdConfigDashboard] Using stored site:', storedSite);
        setCurrentSite(storedSite);
      } else if (!currentSiteExists) {
        // Current site doesn't exist, auto-select first available site
        const firstSite = sites[0];
        console.log('🔍 [AdConfigDashboard] Auto-selecting first site:', firstSite.id);
        setCurrentSite(firstSite.id);
        localStorage.setItem('dashboard_selected_site', firstSite.id);
      } else {
        // Current site exists, store it as preference
        localStorage.setItem('dashboard_selected_site', currentSite);
      }
    }
  }, [sites, sitesLoading, sitesError, currentSite]);

  // Load floors from unified config when site changes
  useEffect(() => {
    if (config && config.floors && activeView === 'floors') {
      setFloors(config.floors);
      setFloorsLoading(false);
    }
  }, [config, activeView]);



  // Process placements data for table
  const placementsData = React.useMemo(() => {
    console.log('🔍 [placementsData] Called with config:', config);
    
    if (!config) {
      console.warn('⚠️ [placementsData] No config available');
      return [];
    }
    
    if (!config.placements) {
      console.warn('⚠️ [placementsData] No placements in config');
      return [];
    }
    
    if (typeof config.placements !== 'object' || config.placements === null) {
      console.error('❌ [placementsData] Invalid placements data type:', typeof config.placements, config.placements);
      return [];
    }
    
    // Additional safety check
    try {
      console.log('🔍 [placementsData] Processing placements:', Object.keys(config.placements));
      return Object.entries(config.placements).map(([name, placement]) => {
        // Ensure placement is valid
        if (!placement || typeof placement !== 'object') {
          console.warn(`Invalid placement data for ${name}:`, placement);
          return null;
        }
        
        // CRITICAL FIX: Add defensive checks for placement.contexts
        if (!placement.contexts || typeof placement.contexts !== 'object') {
          console.warn(`Invalid or missing contexts for placement ${name}:`, placement.contexts);
          return null;
        }
        
        const enabledContexts = Object.entries(placement.contexts)
        .filter(([, ctx]) => ctx && typeof ctx === 'object' && ctx.enabled)
        .map(([contextName]) => contextName);
      
      const isActive = enabledContexts.length > 0;
      
      const desktopSizes = [];
      const mobileSizes = [];
      const bidders = [];
      
      // Extract sizes and bidders from contexts - use safe property access to prevent crashes
      Object.entries(placement.contexts).forEach(([contextName, context]) => {
        if (context && typeof context === 'object' && context.enabled) {
          if (Array.isArray(context.sizes?.desktop)) {
            desktopSizes.push(...context.sizes.desktop.map(size => 
              Array.isArray(size) && size.length >= 2 ? `${size[0]}x${size[1]}` : 'invalid'
            ));
          }
          if (Array.isArray(context.sizes?.mobile)) {
            mobileSizes.push(...context.sizes.mobile.map(size => 
              Array.isArray(size) && size.length >= 2 ? `${size[0]}x${size[1]}` : 'invalid'
            ));
          }
          if (context.bidderParams) {
            bidders.push(...Object.keys(context.bidderParams));
          }
        }
      });
      
      // Determine placement type
      let type = 'other';
      if (name.includes('billboard')) type = 'billboard';
      else if (name.includes('sidebar')) type = 'sidebar';
      else if (name.includes('inarticle')) type = 'in-article';
      
      const placementData = {
        name,
        type,
        contexts: enabledContexts,
        desktopSizes: [...new Set(desktopSizes)],
        mobileSizes: [...new Set(mobileSizes)],
        bidders: [...new Set(bidders)],
        isActive,
        data: placement
      };
      
      // Apply normalization to ensure data completeness
      return normalizePlacementData(placementData);
    }).filter(Boolean); // Remove null values
    } catch (error) {
      console.error('Error processing placements data:', error);
      return [];
    }
  }, [config?.placements]);

  // Event handlers
  const handleSiteChange = (siteId) => {
    console.log('🔄 [Dashboard] Site change initiated from', currentSite, 'to', siteId);
    
    // CRITICAL FIX: Clear all site-specific state immediately to prevent cross-contamination
    setCurrentSite(siteId);
    setSelectedPlacements([]);
    setEditModalOpen(false);
    setEditingPlacement(null);
    setEditingPlacementName(null);
    setFloorModalOpen(false);
    setFloors(null);
    setFloorsLoading(false);
    
    // Reset active view to prevent stale tab content
    setActiveView('placements');
    
    // Store site preference
    localStorage.setItem('dashboard_selected_site', siteId);
    
    console.log('✅ [Dashboard] Site change completed, state cleared');
  };

  const handleDeploy = async () => {
    setDeploying(true);
    try {
      await deployConfig(currentSite);
      alert('Deployment successful! Configuration has been deployed to CDN.');
    } catch (error) {
      console.error('Deployment failed:', error);
      alert('Deployment failed: ' + error.message);
    } finally {
      setDeploying(false);
    }
  };

  const handleSave = async () => {
    try {
      await updateConfig(config);
      alert('Configuration saved successfully!');
    } catch (error) {
      console.error('Save failed:', error);
      alert('Save failed: ' + error.message);
    }
  };

  const handleNewSite = async () => {
    const siteName = prompt('Enter site name:');
    if (siteName) {
      try {
        await createSite({ name: siteName, template: 'default' });
        alert('Site created successfully!');
        refetch();
      } catch (error) {
        console.error('Site creation failed:', error);
        alert('Site creation failed: ' + error.message);
      }
    }
  };
  
  const handleBidderManager = () => {
    setBidderManagerOpen(true);
  };
  
  const handleApplyBiddersToSites = async (updates) => {
    try {
      // Apply bidder changes to each selected site
      for (const update of updates) {
        const site = sites.find(s => s.id === update.siteId);
        if (!site || !site.config) continue;
        
        const updatedBidders = { ...site.config.bidders };
        
        // Enable or disable bidders based on action
        update.bidders.forEach(bidderName => {
          if (updatedBidders[bidderName]) {
            // Bidder exists, just update enabled status
            updatedBidders[bidderName].enabled = update.action === 'enable';
          } else if (update.action === 'enable' && AVAILABLE_BIDDERS[bidderName]) {
            // Bidder doesn't exist but we're enabling it, create with default config
            updatedBidders[bidderName] = {
              ...AVAILABLE_BIDDERS[bidderName].defaultConfig,
              enabled: true
            };
            console.log(`[BidderManager] Created new bidder config for ${bidderName} on site ${update.siteId}`);
          }
          // If disabling and bidder doesn't exist, nothing to do (already disabled)
        });
        
        // Update the site configuration
        const updatedConfig = {
          ...site.config,
          bidders: updatedBidders
        };
        
        await updateConfigApi(update.siteId, updatedConfig);
      }
      
      // Refresh the current site's config if it was updated
      if (updates.some(u => u.siteId === currentSite)) {
        await refetch();
      }
    } catch (error) {
      console.error('Failed to apply bidder changes:', error);
      alert('Failed to apply bidder changes: ' + error.message);
    }
  };

  const handleEditPlacement = (placementName) => {
    const placement = config.placements[placementName];
    setEditingPlacement(placement);
    setEditingPlacementName(placementName);
    setEditModalOpen(true);
  };

  const handleDeletePlacement = async (placementName) => {
    if (window.confirm(`Are you sure you want to delete placement "${placementName}"?`)) {
      try {
        // Create updated placements object without the deleted placement
        const updatedPlacements = Object.fromEntries(
          Object.entries(config.placements).filter(([key]) => key !== placementName)
        );
        
        // Save updated placements to backend
        await updatePlacements(currentSite, updatedPlacements);
        alert(`Placement "${placementName}" deleted successfully!`);
        
        // Refresh to get updated data
        await refetch(currentSite);
      } catch (error) {
        console.error('Failed to delete placement:', error);
        alert('Failed to delete placement: ' + error.message);
      }
    }
  };

  const handleAddPlacement = async () => {
    const placementName = prompt('Enter placement name:');
    if (placementName) {
      try {
        // Use normalized template to ensure complete data structure
        const newPlacement = createPlacementTemplate(placementName, currentSite, '1');
        
        // Create updated placements object with new placement
        const updatedPlacements = {
          ...config.placements,
          [placementName]: newPlacement
        };
        
        // Save updated placements to backend
        await updatePlacements(currentSite, updatedPlacements);
        alert(`Placement "${placementName}" added successfully!`);
        
        // Refresh to get updated data
        await refetch(currentSite);
      } catch (error) {
        console.error('Failed to add placement:', error);
        alert('Failed to add placement: ' + error.message);
      }
    }
  };

  const handleSavePlacement = async (placementName, placementData) => {
    try {
      console.log('Saving placement:', placementName, placementData);
      console.log('Current config placements:', config.placements);
      
      const newPlacements = {
        ...config.placements,
        [placementName]: placementData
      };
      
      console.log('New placements to save:', newPlacements);
      
      await updatePlacements(currentSite, newPlacements);
      await refetch(currentSite);
      setEditModalOpen(false);
      setEditingPlacement(null);
      setEditingPlacementName(null);
    } catch (error) {
      console.error('Failed to save placement:', error);
      alert('Failed to save placement: ' + error.message);
    }
  };

  // Floor management functions (unified config)
  const handleEditFloors = () => {
    if (config && config.floors) {
      setFloors(config.floors);
    }
    setFloorModalOpen(true);
  };

  const handleSaveFloors = async (floorData) => {
    try {
      console.log('[handleSaveFloors] Attempting to save floor data:', floorData);
      console.log('[handleSaveFloors] Current site:', currentSite);
      
      // Update floors using the dedicated API endpoint
      const result = await updateFloors(currentSite, floorData);
      console.log('[handleSaveFloors] API response:', result);
      
      // Update local state
      setFloors(floorData);
      
      // Refetch the configuration to ensure everything is in sync
      refetch();
      
      setFloorModalOpen(false);
      alert('Floor configuration saved successfully!');
    } catch (error) {
      console.error('[handleSaveFloors] Failed to save floors:', error);
      console.error('[handleSaveFloors] Error details:', {
        message: error.message,
        stack: error.stack,
        floorData: floorData
      });
      alert('Failed to save floors: ' + error.message);
    }
  };

  // Loading states
  if (sitesLoading || configLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading configuration...</p>
        </div>
      </div>
    );
  }

  // Error states
  if (sitesError || configError) {
    const error = configError || sitesError;
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="max-w-2xl mx-auto p-6">
          <ValidationErrorDisplay
            error={error}
            title={configError ? "Configuration Error" : "Sites Loading Error"}
            type="error"
            className="mb-6"
          />
          <div className="text-center">
            <button 
              onClick={() => window.location.reload()} 
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="container mx-auto p-6">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Prebid Multi-Site Dashboard</h1>
              <p className="text-gray-600">Manage ad placements and configurations across all sites</p>
            </div>
            <ActionButtons
              onSave={handleSave}
              onDeploy={handleDeploy}
              onNewSite={handleNewSite}
              onBidderManager={handleBidderManager}
              deploying={deploying}
            />
          </div>
          
          <SiteSelector
            sites={sites}
            currentSite={currentSite}
            onSiteChange={handleSiteChange}
            sitesLoading={sitesLoading}
            sitesError={sitesError}
          />
        </div>

        {/* Tab Navigation */}
        <TabNavigation
          activeView={activeView}
          onViewChange={setActiveView}
        />

        {/* Content */}
        <div className="mt-8">
          {activeView === 'site' && (
            <SiteSettings
              config={config}
              onUpdateConfig={updateConfig}
              isLoading={configLoading}
            />
          )}

          {activeView === 'placements' && (
            <PlacementTable
              placements={placementsData}
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              typeFilter={typeFilter}
              onTypeFilterChange={setTypeFilter}
              statusFilter={statusFilter}
              onStatusFilterChange={setStatusFilter}
              onEditPlacement={handleEditPlacement}
              onDeletePlacement={handleDeletePlacement}
              onAddPlacement={handleAddPlacement}
              selectedPlacements={selectedPlacements}
              onSelectionChange={setSelectedPlacements}
            />
          )}

          {activeView === 'floors' && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Floor Price Management</h2>
                <button
                  onClick={handleEditFloors}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Edit Floors
                </button>
              </div>
              
              {floorsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : floors ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="font-medium text-gray-900 mb-2">Default Floor</h3>
                      <p className="text-2xl font-bold text-blue-600">{floors.default || 0.25} {floors.currency || 'EUR'}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="font-medium text-gray-900 mb-2">Currency</h3>
                      <p className="text-2xl font-bold text-green-600">{floors.currency || 'EUR'}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h3 className="font-medium text-gray-900 mb-2">Status</h3>
                      <p className="text-2xl font-bold text-purple-600">{floors.enabled ? 'Enabled' : 'Disabled'}</p>
                    </div>
                  </div>
                  
                  {floors.overrides && Object.keys(floors.overrides).length > 0 && (
                    <div>
                      <h3 className="font-medium text-gray-900 mb-3">Placement Overrides</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {Object.entries(floors.overrides).map(([placement, override]) => (
                          <div key={placement} className="bg-gray-50 p-3 rounded-lg">
                            <h4 className="font-medium text-gray-800 mb-1">{placement}</h4>
                            <p className="text-sm text-gray-600">
                              Desktop: {override.desktop || floors.default} {floors.currency}
                            </p>
                            <p className="text-sm text-gray-600">
                              Mobile: {override.mobile || floors.default} {floors.currency}
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No floor configuration found
                </div>
              )}
            </div>
          )}

          {activeView === 'prebid' && (
            <PrebidConfig
              config={config}
              onUpdateConfig={updateConfig}
              isLoading={configLoading}
            />
          )}
          {activeView === 'privacy' && (
            <PrivacyStorageTab
              config={config}
              onUpdateConfig={updateConfig}
              isLoading={configLoading}
            />
          )}
          {activeView === 'management' && (
            <SiteManagementTab
              sites={sites}
              onCreateSite={createSite}
              onDeleteSite={async (siteId) => {
                // Call the delete API and refetch sites
                const response = await fetch(`https://api.dimitriu.xyz/api/sites/${siteId}`, {
                  method: 'DELETE',
                  credentials: 'include',
                  headers: {
                    'Content-Type': 'application/json'
                  }
                });
                if (!response.ok) {
                  throw new Error(`Failed to delete site: ${response.statusText}`);
                }
                // Refresh sites list using proper state management
                await refetchSites();
                // If we deleted the current site, switch to another site or default
                if (siteId === currentSite && sites.length > 1) {
                  const remainingSites = sites.filter(site => site.id !== siteId);
                  if (remainingSites.length > 0) {
                    setCurrentSite(remainingSites[0].id);
                  }
                }
              }}
              onCloneSite={async (siteId, newName) => {
                // TODO: Implement clone functionality
                throw new Error('Clone functionality not yet implemented');
              }}
              onExportSite={(site) => {
                // Export handled in the component itself
                return true;
              }}
              onImportSite={async (configData) => {
                // TODO: Implement import functionality
                throw new Error('Import functionality not yet implemented');
              }}
              onSelectSite={(siteId) => {
                setCurrentSite(siteId);
                setActiveView('placements');
              }}
              isLoading={sitesLoading}
            />
          )}
          
          {activeView === 'analytics' && (
            <AnalyticsTab 
              api={apiClient}
              currentSite={currentSite}
            />
          )}

        </div>

        {/* Modals */}
        <PlacementEditModal
          placement={editingPlacement}
          placementName={editingPlacementName}
          isOpen={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            setEditingPlacement(null);
            setEditingPlacementName(null);
          }}
          onSave={handleSavePlacement}
          config={config}
        />

        <FloorEditModal
          isOpen={floorModalOpen}
          onClose={() => setFloorModalOpen(false)}
          onSave={handleSaveFloors}
          floors={floors}
          currentSite={currentSite}
          availablePlacements={placementsData.map(p => p.name)}
        />
        
        {bidderManagerOpen && (
          <BidderManager
            sites={sites.map(s => ({
              siteId: s.id,
              siteName: s.name || s.id,
              domain: s.domain || s.id,
              config: s.config || {}
            }))}
            onApplyToSites={handleApplyBiddersToSites}
            onClose={() => setBidderManagerOpen(false)}
          />
        )}
      </div>
    </div>
  );
};

export default AdConfigDashboard;